import React, { useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { FormikHelpers, FormikProps } from 'formik'
import * as Routes from '../../../routes'
import { t } from '../../i18n'
import CommentsPanel from '../shared/comments/CommentsPanel'
import { renderRatingMeta } from '../shared/comments/RatingSummary'
import { CommentsSection } from '../shared/comments/types'
import SectionTitle from '../shared/sectionTitle'
import { DosingLimits } from '../shared/dosingLimits'
import DosingModeUsagePanel, {
  DosingModeUsage
} from '../shared/dosing_modes/DosingModeUsagePanel'
import ConfirmModal from '../shared/versioning/ConfirmModal'
import CopyFromModal from '../shared/versioning/CopyFromModal'
import PublishConfirmModal from '../shared/versioning/PublishConfirmModal'
import PublishImpactBody from '../shared/versioning/PublishImpactBody'
import ReadOnlyNotice from '../shared/versioning/ReadOnlyNotice'
import VersionActionsBar from '../shared/versioning/VersionActionsBar'
import VersionsPanel from '../shared/versioning/VersionsPanel'
import { VersionStatusBadge } from '../shared/versioning/badges'
import useCopyFromSource from '../shared/versioning/useCopyFromSource'
import useSaveAndPublish from '../shared/versioning/useSaveAndPublish'
import FeederDosingModeForm, { FORM_ID } from './FeederDosingModeForm'
import { railsFieldErrors } from '../shared/forms/railsFieldErrors'
import { ServerErrorsStatus } from '../shared/forms/useFieldError'
import {
  CopySourceFeederDosingMode,
  FeederDosingModeErrors,
  FeederDosingModeRecord,
  FeederDosingModeValues,
  VersioningProps,
  copyParameters,
  feederDosingModePayload,
  feederDosingModeValues
} from './feederDosingModeValues'

const scope = 'feeder_dosing_modes'

type SaveOutcome = 'saved' | 'invalid' | 'failed'

interface SaveResult {
  outcome: SaveOutcome
  messages: string[]
}

const SAVE_OUTCOME_MESSAGES: Record<SaveOutcome, string> = {
  saved: 'save_successful',
  invalid: 'save_failed',
  failed: 'save_error'
}

// Only a validation failure (422) carries our JSON errors; a permission denial answers with the
// HTML 404 page and a server error with whatever Rails renders, so neither has field errors.
const validationErrors = (error: unknown): FeederDosingModeErrors | null =>
  axios.isAxiosError<FeederDosingModeErrors>(error) &&
  error.response?.status === 422 &&
  typeof error.response.data === 'object'
    ? error.response.data
    : null

const saveRequest = (values: FeederDosingModeValues) => {
  const payload = feederDosingModePayload(values)
  return values.id
    ? axios.put<FeederDosingModeRecord>(
        Routes.admin_feeder_dosing_mode_path(values.id),
        payload
      )
    : axios.post<FeederDosingModeRecord>(
        Routes.admin_feeder_dosing_modes_path(),
        payload
      )
}

const FeederDosingModePage = ({
  feederDosingMode,
  limits,
  readOnly = false,
  commentsSection,
  versioning,
  usage
}: {
  feederDosingMode?: FeederDosingModeRecord
  limits: DosingLimits
  readOnly?: boolean
  commentsSection?: CommentsSection
  versioning?: VersioningProps
  usage?: DosingModeUsage
}) => {
  const [record, setRecord] = useState(feederDosingMode)
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const formikRef = useRef<FormikProps<FeederDosingModeValues>>(null)
  // Formik reinitialises whenever this changes, so it must only change when a save returns a new record
  const initialValues = useMemo(() => feederDosingModeValues(record), [record])
  const { confirmPublish, afterSaved, cancelPending } =
    useSaveAndPublish<FeederDosingModeValues>({
      formikRef,
      publishUrl: Routes.publish_admin_feeder_dosing_mode_path,
      publishFailedText: t('versioning.publish_failed'),
      onPublishError: (messages) => {
        setSubmitting(false)
        setSaveResult({ outcome: 'failed', messages })
      }
    })
  const copyFrom = useCopyFromSource(
    formikRef,
    (source: CopySourceFeederDosingMode) => {
      const formik = formikRef.current
      if (formik) formik.setValues(copyParameters(formik.values, source))
    }
  )

  const handleSubmit = async (
    values: FeederDosingModeValues,
    helpers: FormikHelpers<FeederDosingModeValues>
  ) => {
    setSubmitting(true)
    try {
      const response = await saveRequest(values)
      if (!values.id) {
        window.location.href = Routes.edit_admin_feeder_dosing_mode_path(
          response.data.id
        )
        return
      }
      setRecord(response.data)
      helpers.setStatus({})
      if (afterSaved(response.data.id)) return
      setSubmitting(false)
      setSaveResult({ outcome: 'saved', messages: [] })
    } catch (error) {
      cancelPending()
      setSubmitting(false)
      const errors = validationErrors(error)
      const status: ServerErrorsStatus = {
        serverErrors: railsFieldErrors(errors?.field_errors ?? {})
      }
      helpers.setStatus(status)
      setSaveResult({
        outcome: errors ? 'invalid' : 'failed',
        messages: errors?.errors ?? []
      })
    }
  }

  const confirmAndPublish = () => {
    setShowPublishConfirm(false)
    confirmPublish('publish')
  }

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <SectionTitle
            rows={[
              record?.id ? 'Edit' : 'New',
              t('feeder_dosing_mode', { scope })
            ]}
          />
          {record && (
            <span className="ms-3">
              <VersionStatusBadge
                status={record.status}
                versionNumber={record.version_number}
              />
            </span>
          )}
        </div>
        <VersionActionsBar
          actions={versioning?.actions}
          readOnly={readOnly}
          submitting={submitting}
          formId={FORM_ID}
          indexHref={Routes.admin_feeder_dosing_modes_path()}
          deleteDraftUrl={
            record ? Routes.admin_feeder_dosing_mode_path(record.id) : undefined
          }
          createNewVersionUrl={
            record
              ? Routes.create_new_version_draft_admin_feeder_dosing_mode_path(
                  record.id
                )
              : undefined
          }
          deleteConfirmBody={t('delete_draft_confirm_body', { scope })}
          onPublish={() => setShowPublishConfirm(true)}
        >
          <button
            type="button"
            className="btn btn-secondary inverted me-2"
            onClick={copyFrom.openPicker}
          >
            {t('copy_from_mode.button', { scope })}
          </button>
        </VersionActionsBar>
      </div>
      {readOnly && record && <ReadOnlyNotice />}
      <div className="card card-robis">
        <div className="dosing-mode-layout">
          <div className="dosing-mode-layout__form">
            {saveResult && (
              <div
                className={`alert alert-${saveResult.outcome === 'saved' ? 'success' : 'danger'}`}
                role="alert"
              >
                {t(SAVE_OUTCOME_MESSAGES[saveResult.outcome], { scope })}
                {saveResult.messages.length > 0 && (
                  <ul className="mb-0 mt-2">
                    {saveResult.messages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <FeederDosingModeForm
              initialValues={initialValues}
              limits={limits}
              readOnly={readOnly}
              formikRef={formikRef}
              onSubmit={handleSubmit}
            />
          </div>
          {record?.id && (versioning || commentsSection) && (
            <div className="dosing-mode-layout__side">
              {versioning && (
                <VersionsPanel
                  versions={versioning.versions}
                  canRestore={versioning.actions.canRestore}
                  versionHref={(version) =>
                    Routes.edit_admin_feeder_dosing_mode_path(version.id)
                  }
                  restoreUrl={(version) =>
                    Routes.create_draft_from_version_admin_feeder_dosing_mode_path(
                      version.id
                    )
                  }
                  renderMeta={renderRatingMeta}
                />
              )}
              {commentsSection && (
                <CommentsPanel
                  commentsUrl={Routes.admin_feeder_dosing_mode_comments_path(
                    record.id
                  )}
                  tagOptionsUrl={Routes.tag_options_admin_feeder_dosing_mode_comments_path(
                    record.id
                  )}
                  initialComments={commentsSection.comments}
                  abilities={commentsSection.abilities}
                  tagging={commentsSection.tagging}
                  placeholder={t('comment_placeholder', { scope })}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {record?.id && usage && <DosingModeUsagePanel usage={usage} />}
      {copyFrom.pickerOpen && (
        <CopyFromModal<CopySourceFeederDosingMode>
          url={Routes.copy_sources_admin_feeder_dosing_modes_path()}
          params={{ exclude_feeder_dosing_mode_id: record?.id ?? null }}
          scope="feeder_dosing_modes.copy_from_mode"
          onSelect={copyFrom.handlePicked}
          onClose={copyFrom.closePicker}
        />
      )}
      {copyFrom.overwriteSource && (
        <ConfirmModal
          title={t('copy_from_mode.overwrite_title', { scope })}
          confirmActions={[
            {
              label: t('copy_from_mode.overwrite_confirm', { scope }),
              className: 'btn btn-primary',
              onConfirm: copyFrom.confirmOverwrite
            }
          ]}
          onClose={copyFrom.cancelOverwrite}
        >
          <p className="mb-0">
            {t('copy_from_mode.overwrite_text', { scope })}
          </p>
        </ConfirmModal>
      )}
      {showPublishConfirm && versioning && (
        <PublishConfirmModal
          onPublish={confirmAndPublish}
          onClose={() => setShowPublishConfirm(false)}
        >
          <PublishImpactBody
            usedIntro={t('publish_confirm_products', { scope })}
            usedItems={versioning.usedByProducts}
            noUsageText={t('publish_confirm_no_products', { scope })}
            locationsIntro={t('publish_confirm_locations', { scope })}
            locations={versioning.affectedLocations}
            noLocationsText={t('publish_confirm_no_locations', { scope })}
          />
        </PublishConfirmModal>
      )}
    </>
  )
}

export default FeederDosingModePage
